<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4"
      @click.self="close"
    >
      <section
        class="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="upload-report-title"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <h2
              id="upload-report-title"
              class="text-xl font-black text-slate-900"
            >
              {{ mode === "update" ? "Update Report" : "Upload Report" }} — {{ monthLabel }}
            </h2>
            <p class="mt-1 text-sm text-slate-500">
              {{ clientName }}<span v-if="clientCode"> · {{ clientCode }}</span>
            </p>
          </div>
          <button
            type="button"
            class="text-2xl text-slate-400"
            :disabled="busy"
            aria-label="Close"
            @click="close"
          >
            ×
          </button>
        </div>

        <div class="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
          <p><strong>Month:</strong> {{ monthLabel }}</p>
          <p v-if="period">
            <strong>Reporting period:</strong> {{ period }}
          </p>
          <p
            v-if="mode === 'update'"
            class="mt-2 text-amber-700"
          >
            Uploading this PDF will replace the current active report after the upload succeeds.
          </p>
        </div>

        <label
          for="report-pdf"
          class="mt-5 flex cursor-pointer flex-col items-center rounded-xl border-2 border-dashed border-slate-300 px-5 py-8 text-center hover:border-blue-400"
        >
          <FileUp
            class="h-8 w-8 text-blue-600"
            aria-hidden="true"
          />
          <span class="mt-2 font-bold text-slate-800">Choose a PDF report</span>
          <span class="mt-1 text-xs text-slate-500">PDF only, maximum 25 MB</span>
          <input
            id="report-pdf"
            ref="fileInput"
            type="file"
            accept="application/pdf,.pdf"
            class="sr-only"
            :disabled="busy"
            @change="selectFile"
          >
        </label>
        <div
          v-if="file"
          class="mt-3 flex items-center justify-between rounded-lg bg-blue-50 px-3 py-2 text-sm text-blue-900"
        >
          <span class="truncate">{{ file.name }} · {{ formatSize(file.size) }}</span>
          <button
            type="button"
            class="ml-3 font-bold"
            :disabled="busy"
            @click="removeFile"
          >
            Remove
          </button>
        </div>
        <p
          v-if="validationError"
          class="mt-2 text-sm font-semibold text-red-600"
          role="alert"
        >
          {{ validationError }}
        </p>
        <div
          v-if="busy"
          class="mt-4"
        >
          <div class="mb-1 flex justify-between text-xs font-bold text-slate-600">
            <span>Uploading…</span><span>{{ progress }}%</span>
          </div>
          <div
            class="h-2 overflow-hidden rounded-full bg-slate-200"
            role="progressbar"
            :aria-valuenow="progress"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            <div
              class="h-full bg-blue-600 transition-all"
              :style="{ width: `${progress}%` }"
            />
          </div>
        </div>
        <p
          v-if="serverError"
          class="mt-3 text-sm font-semibold text-red-600"
          role="alert"
        >
          {{ serverError }}
        </p>
        <div class="mt-6 flex justify-end gap-3">
          <button
            type="button"
            class="rounded-lg border border-slate-300 px-4 py-2 font-bold text-slate-700"
            :disabled="busy"
            @click="close"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-lg bg-[#173b5f] px-4 py-2 font-bold text-white disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="!file || !!validationError || busy"
            @click="submit"
          >
            {{ mode === "update" ? "Update Report" : "Upload Report" }}
          </button>
        </div>
      </section>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { FileUp } from "lucide-vue-next";
import type { ReportRecord } from "@/types/reports";
import { replaceReportPdf, uploadReportPdf } from "@/services/reportsService";

const props = defineProps<{ open: boolean; mode: "upload" | "update"; clientId: string; clientCode?: string | null; clientName: string; monthNumber: number; monthLabel: string; dateFrom?: string | null; dateTo?: string | null; existingReport?: ReportRecord | null }>();
const emit = defineEmits<{ (event: "update:open", value: boolean): void; (event: "uploaded", report: ReportRecord): void; (event: "updated", report: ReportRecord): void }>();
const file = ref<File | null>(null); const validationError = ref(""); const serverError = ref(""); const busy = ref(false); const progress = ref(0); const fileInput = ref<HTMLInputElement | null>(null);
const period = computed(() => [props.dateFrom, props.dateTo].filter(Boolean).join(" – "));
watch(() => props.open, (open) => { if (open) { file.value = null; validationError.value = ""; serverError.value = ""; progress.value = 0; } });
function close() { if (!busy.value) emit("update:open", false); }
function validate(candidate: File | null) { if (!candidate) return "Please choose a PDF file."; if (candidate.size <= 0) return "The selected file is empty."; if (candidate.size > 25 * 1024 * 1024) return "The PDF exceeds the maximum allowed file size of 25 MB."; if (candidate.type && candidate.type !== "application/pdf") return "Only PDF files are allowed."; if (!candidate.name.toLowerCase().endsWith(".pdf")) return "Only PDF files are allowed."; return ""; }
function selectFile(event: Event) { const candidate = (event.target as HTMLInputElement).files?.[0] || null; validationError.value = validate(candidate); file.value = validationError.value ? null : candidate; serverError.value = ""; }
function removeFile() { file.value = null; if (fileInput.value) fileInput.value.value = ""; }
function formatSize(bytes: number) { return `${(bytes / 1024 / 1024).toFixed(2)} MB`; }
function uploadErrorMessage(error: any) {
  const message = error?.response?.data?.message;
  if (message === "The report failed to upload.") {
    return "The PDF exceeds its configured upload limit. Please try a smaller PDF or contact an administrator.";
  }
  return message || "Unable to upload the PDF. Please try again.";
}
async function submit() { if (!file.value) return; busy.value = true; serverError.value = ""; progress.value = 0; const form = new FormData(); form.append("report", file.value); form.append("client_id", props.clientId); form.append("month_number", String(props.monthNumber)); form.append("report_month_label", props.monthLabel); if (props.dateFrom) form.append("date_from", props.dateFrom); if (props.dateTo) form.append("date_to", props.dateTo); try { const report = props.mode === "update" && props.existingReport ? await replaceReportPdf(props.existingReport.id, form, (value) => progress.value = value) : await uploadReportPdf(form, (value) => progress.value = value); emit(props.mode === "update" ? "updated" : "uploaded", report); emit("update:open", false); } catch (error: any) { serverError.value = uploadErrorMessage(error); } finally { busy.value = false; } }
</script>
